import React, { useEffect, useState } from "react";
import {
  Box, Text, VStack, HStack, Icon, Spinner, Center,
} from "@chakra-ui/react";
import { FiBell, FiInfo, FiAlertTriangle } from "react-icons/fi";
import moment from "moment";
import { PageHeader } from "./portalUi";
import { NoticeAttachments } from "./NoticeAttachments";
import {
  fetchNoticesForFullCertEnrollments,
  fetchNoticesForCourseRun,
  fetchCollegeNotices,
  sanitizeNoticeHtml,
  getNoticeCardStyle,
} from "../utils/lmsNotices";

const typeIcons = {
  info: FiInfo,
  warning: FiAlertTriangle,
  alert: FiBell,
};

const noticeBodySx = {
  fontSize: { base: "2xs", md: "sm" },
  color: "gray.600",
  mb: 2,
  lineHeight: "tall",
  "& p": { mb: 2 },
  "& p:last-child": { mb: 0 },
  "& ul": { pl: 5, my: 1, listStyleType: "disc" },
  "& ol": { pl: 5, my: 1, listStyleType: "decimal" },
  "& strong, & b": { fontWeight: 600 },
  "& em, & i": { fontStyle: "italic" },
  "& a": { color: "blue.600", textDecoration: "underline" },
};

/** Pull AM / PM / SAT from run id, run_name, or code. */
function getRunSlotLabel(...sources) {
  const text = sources.filter(Boolean).join(" ");
  const match = String(text).match(/\b(AM|PM|SAT)\b/i);
  return match ? match[1].toUpperCase() : null;
}

/** Parse Batch N + AM/PM from a run id like DCG-BATCH42-AM. */
function parseBatchFromRunId(runId) {
  if (!runId) return null;
  const batchMatch = String(runId).match(/BATCH\s*(\d+)/i);
  const slot = getRunSlotLabel(runId);
  if (!batchMatch && !slot) return null;
  const parts = [];
  if (batchMatch) parts.push(`Batch ${batchMatch[1]}`);
  if (slot) parts.push(slot);
  return parts.join(" ");
}

/** Resolve friendly cert name for a notice; fall back to code only if no name. */
function getNoticeCertLabel(notice, certifications = []) {
  if (!notice) return null;

  const byRun = notice.full_cert_run_id
    ? certifications.find((c) => c?.id === notice.full_cert_run_id)
    : null;

  let certName = byRun?.full_certification?.name || null;
  const typeCode = notice.full_cert_type_code
    || byRun?.full_certification?.code
    || null;

  if (!certName && typeCode) {
    const byType = certifications.find(
      (c) => c?.full_certification?.code === typeCode,
    );
    certName = byType?.full_certification?.name || typeCode;
  }

  if (!certName) return null;

  // Run-specific: append "Batch {intake} AM/PM"
  if (notice.full_cert_run_id) {
    const parts = [];
    if (byRun?.intake != null && String(byRun.intake).trim() !== "") {
      parts.push(`Batch ${byRun.intake}`);
      const slot = getRunSlotLabel(byRun.id, byRun.run_name, byRun.code);
      if (slot) parts.push(slot);
    } else {
      const fromId = parseBatchFromRunId(notice.full_cert_run_id);
      if (fromId) return `${certName} · ${fromId}`;
    }
    if (parts.length) return `${certName} · ${parts.join(" ")}`;
  }

  return certName;
}

/**
 * @param {'full_cert'|'course'} props.mode
 * @param {Array} [props.certifications] — for dashboard full-cert notices
 * @param {string} [props.courseTypeCode] — for EachCourse
 * @param {string} [props.courseRunId] — for EachCourse
 * @param {boolean} [props.showHeader=true]
 * @param {boolean} [props.includeCollege=true] — prepend college-wide notices
 */
export const NoticesTab = ({
  mode = "full_cert",
  certifications = [],
  courseTypeCode,
  courseRunId,
  showHeader = true,
  includeCollege = true,
}) => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scopeKey =
    mode === "course"
      ? `course:${courseTypeCode || ""}:${courseRunId || ""}`
      : `cert:${(certifications || [])
          .map((c) => `${c?.id || ""}:${c?.full_certification?.code || ""}`)
          .join("|")}`;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const collegePromise = includeCollege ? fetchCollegeNotices() : Promise.resolve([]);
        let scopedPromise;
        if (mode === "course") {
          scopedPromise = fetchNoticesForCourseRun({ courseTypeCode, courseRunId });
        } else {
          scopedPromise = fetchNoticesForFullCertEnrollments(certifications);
        }
        const [college, scoped] = await Promise.all([collegePromise, scopedPromise]);
        const merged = [...(college || []), ...(scoped || [])];
        if (!cancelled) setNotices(merged);
      } catch (err) {
        console.error("Error loading notices:", err);
        if (!cancelled) {
          setError("Could not load notices.");
          setNotices([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, scopeKey, courseTypeCode, courseRunId, includeCollege]);

  const displayDate = (notice) =>
    notice.published_at || notice.starts_at || notice.created_at;

  const isCollege = (notice) =>
    !notice.course_type_code &&
    !notice.course_run_id &&
    !notice.full_cert_type_code &&
    !notice.full_cert_run_id;

  return (
    <VStack spacing={{ base: 2.5, md: 3 }} align="stretch">
      {showHeader && (
        <PageHeader
          icon={FiBell}
          iconColor="purple.500"
          title="Notices"
        />
      )}

      {loading && (
        <Center py={8}>
          <Spinner size="md" color="purple.400" />
        </Center>
      )}

      {!loading && error && (
        <Box
          p={3}
          borderRadius="xl"
          border="1px solid"
          borderColor="red.100"
          bg="red.50"
        >
          <Text fontSize="sm" color="red.600">{error}</Text>
        </Box>
      )}

      {!loading && !error && notices.length === 0 && (
        <Box
          p={{ base: 3, md: 4 }}
          borderRadius="xl"
          border="1px dashed"
          borderColor="gray.200"
          bg="white"
        >
          <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
            No notices right now.
          </Text>
        </Box>
      )}

      {!loading && !error && notices.length > 0 && (
        <VStack spacing={{ base: 2, md: 2.5 }} align="stretch">
          {notices.map((notice) => {
            const college = isCollege(notice);
            const style = getNoticeCardStyle(notice.notice_type, { college });
            const NoticeIcon = college ? FiBell : (typeIcons[notice.notice_type] || FiInfo);
            const bodyHtml = sanitizeNoticeHtml(notice.body);
            const certLabel =
              mode === "full_cert" && !college
                ? getNoticeCertLabel(notice, certifications)
                : null;
            return (
              <Box
                key={notice.id}
                p={{ base: 2.5, md: 3 }}
                borderRadius="xl"
                border="1px solid"
                borderColor={style.border}
                bg={style.bg}
              >
                <HStack align="flex-start" spacing={{ base: 2.5, md: 3 }}>
                  <Icon
                    as={NoticeIcon}
                    color={style.iconColor}
                    boxSize={5}
                    mt={0.5}
                    flexShrink={0}
                  />
                  <Box flex="1" minW={0}>
                    <HStack spacing={2} mb={certLabel ? 0.5 : 1} flexWrap="wrap" align="center">
                      <Text
                        fontWeight="700"
                        fontSize={{ base: "xs", md: "md" }}
                        color="gray.800"
                        lineHeight="short"
                      >
                        {notice.title}
                      </Text>
                      <Box
                        as="span"
                        fontSize="2xs"
                        fontWeight="600"
                        borderRadius="full"
                        px={2.5}
                        py={0.5}
                        bg={style.badgeBg}
                        color={style.badgeColor}
                      >
                        {style.label}
                      </Box>
                    </HStack>
                    {certLabel && (
                      <Text
                        fontSize={{ base: "2xs", md: "xs" }}
                        color="gray.500"
                        fontWeight="500"
                        mb={1.5}
                        noOfLines={1}
                      >
                        {certLabel}
                      </Text>
                    )}
                    {bodyHtml ? (
                      <Box
                        mb={2}
                        sx={noticeBodySx}
                        dangerouslySetInnerHTML={{ __html: bodyHtml }}
                      />
                    ) : null}
                    <NoticeAttachments attachments={notice.attachments} />
                    {displayDate(notice) && (
                      <Text fontSize="2xs" color="gray.400">
                        {moment(displayDate(notice)).format("DD MMM YYYY")}
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};
